import { useEffect, useState } from 'react';
import { SHOW_AUTH } from '../config/flags';

const API = process.env.REACT_APP_BACKEND_URL;

/** Lightweight admin-role check backed by GET /api/auth/me. Cached in memory
 *  so multiple components on the same page only make one round-trip. */
let cachedRole = null;
let inFlight = null;

export const fetchRole = async () => {  // App Store v1: auth is hidden — short-circuit so we make ZERO
  // network calls to /api/auth/*. Every admin edit affordance mounts
  // this hook, and unguarded they collectively fire ~17 requests per
  // cold page load (see /app/test_reports/iteration_106.json).
  if (!SHOW_AUTH) return (cachedRole = 'anonymous');
  if (cachedRole !== null) return cachedRole;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      let res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (res.status === 401) {
        // Access token (15 min) may have expired while the refresh token is
        // still valid — refresh once and retry, otherwise admins silently
        // lose their edit UI mid-session.
        const ref = await fetch(`${API}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (ref.ok) {
          res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        }
      }
      if (!res.ok) return (cachedRole = 'anonymous');
      const data = await res.json();
      cachedRole = data.role || 'user';
      return cachedRole;
    } catch {
      return (cachedRole = 'anonymous');
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};

export const invalidateRoleCache = () => { cachedRole = null; };

export default function useIsAdmin() {
  const [isAdmin, setIsAdmin] = useState(cachedRole === 'admin');
  useEffect(() => {
    let alive = true;
    fetchRole().then((r) => { if (alive) setIsAdmin(r === 'admin'); });
    return () => { alive = false; };
  }, []);
  return isAdmin;
}
